import { useState, useEffect, FC } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import UserManagementGrid from 'src/components/Grids/UserManagementGrid';
import TextField from 'src/components/InputFields/TextField';
import { useFormik } from 'formik';
import RolesField from 'src/components/InputFields/RoleDropdown';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import { userValidationSchema } from 'src/utils/validationScheme.utils';
import { addUserInitialValues } from 'src/utils/initialValues.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { addUser, getCompanyRoles } from 'src/services/user.service';
import { getAllUsers } from 'src/services/user.service';
import { getSubscriptionStatus } from 'src/services/subscription.service';
import {
  dataStorageKeywords,
  dialogTitles,
  dialogSubtitles,
  userMessages,
} from 'src/utils/properties.utils';
import MainLayout from 'src/pages/private';
import SearchBar from 'src/components/InputFields/Searchbar';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  PrimaryActionButton,
  ButtonIcon,
  OverflowContainer,
} from 'src/components/StyledComponents/StyledPageComponents';

const UserManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userAdded, setUserAdded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roles, setRoles] = useState<{ value: string; label: string }[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [isCheckingUserLimit, setIsCheckingUserLimit] = useState(false);

  const { showSnackbar } = StyledSnackbar();

  const formik = useFormik({
    initialValues: addUserInitialValues,
    validationSchema: userValidationSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      const role = await getEncryptedLocalStorageData(
        dataStorageKeywords.HIGHEST_ROLE
      );
      setCurrentUserRole(role);
    };

    fetchCurrentUserRole();
  }, []);

  const handleSubmit = async (values: typeof addUserInitialValues) => {
    setLoading(true);

    try {
      const response = await addUser({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        roles: values.role,
      });

      showSnackbar(response.message, 'success');
      setUserAdded(!userAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = async () => {
    setIsCheckingUserLimit(true);

    try {
      const [subscription, usersResponse] = await Promise.all([
        getSubscriptionStatus(),
        getAllUsers(1, 1000, '', 'firstName', 'ASC'),
      ]);
      const activeUserCount = usersResponse.data.filter(
        (user) => user.isActive
      ).length;
      const paidUserCount = Number(subscription?.paidUserCount || 0);

      if (
        subscription?.status === 'ACTIVE' &&
        paidUserCount > 0 &&
        activeUserCount >= paidUserCount
      ) {
        showSnackbar(userMessages.ACTIVE_USER_LIMIT_REACHED, 'error');
        return;
      }

      const rolesData = await getCompanyRoles();
      setRoles(rolesData);
      setOpen(true);
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsCheckingUserLimit(false);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  return (
    <MainLayout
      title="User Management"
      searchBar={
        <SearchBar
          width="250px"
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search users by name or email"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="145px"
          onClick={handleClickOpen}
          disabled={isCheckingUserLimit}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New User
        </PrimaryActionButton>
      }
    >
      <UserManagementGrid state={userAdded} searchTerm={searchTerm} />
      <Box display="flex">
        <OverflowContainer display="flex" flexDirection="column">
          <CustomDialog
            height={'480px'}
            open={open}
            handleClose={handleClose}
            title={dialogTitles.ADD_USER}
            subtitle={dialogSubtitles.ADD_USER}
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
          >
            <Box display="flex" flexDirection="column">
              <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
                <TextField
                  width="300px"
                  id="firstName"
                  label="First Name"
                  formikProps={formik}
                />
                <TextField
                  width="300px"
                  id="lastName"
                  label="Last Name"
                  formikProps={formik}
                />
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <TextField
                  width="300px"
                  id="email"
                  label="Email"
                  type="email"
                  formikProps={formik}
                />
                <RolesField
                  width="300px"
                  id="role"
                  label="Role"
                  formikProps={formik}
                  options={roles}
                  userRole={currentUserRole || ''}
                  isEditingOwnAccount={false}
                />
              </Box>
            </Box>
          </CustomDialog>
        </OverflowContainer>
      </Box>
    </MainLayout>
  );
};

export default UserManagement;
